import { useState, useEffect } from 'react';

// Material-UI
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell as _TableCell,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { styled, CircularProgress, Typography, Box, Button } from '@mui/material';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CheckIcon from '@mui/icons-material/Check';
import WifiIcon from '@mui/icons-material/Wifi';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import Tooltip from 'components/popups/Tooltip';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';

export default function ScansByEvent() {
    const [state, setState] = useState({
        data: null,
        loading: true,
        error: false,
    });

    const { data, loading, error } = state;

    useEffect(() => {
        fetchExpresso(
            `/admin/reports/scans_by_event?tz=${moment.tz.guess()}&start=${moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss')}&end=${moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss')}`,
        )
            .then(async (res) => {
                if (res.status !== 200) {
                    setState((s) => ({ ...s, loading: false, error: true }));
                    return;
                }
                const data = await res.json();
                setState((s) => ({ ...s, data: data, loading: false, error: false }));
            })
            .catch((e) => {
                setState((s) => ({ ...s, loading: false, error: true }));
            });
    }, []);

    if (loading) {
        return (
            <Root>
                <Status>
                    <CircularProgress disableShrink />
                </Status>
            </Root>
        );
    }

    if (error) {
        return (
            <Root>
                <Status>
                    <Typography>Unable to load scans</Typography>
                </Status>
            </Root>
        );
    }

    return (
        <Root>
            <CardHeader
                title="Scans by Event"
                action={
                    <Box>
                        <Button style={{ textDecoration: 'underline' }}>Today</Button>
                        {/* <Button style={range === 'day' ? {textDecoration: 'underline'} : undefined} onClick={() => setRange('day')}>Today</Button>
                        <Button style={range === 'month' ? {textDecoration: 'underline'} : undefined} onClick={() => setRange('month')}>This Month</Button>
                        <Button style={range === 'year' ? {textDecoration: 'underline'} : undefined} onClick={() => setRange('year')}>This Year</Button> */}
                    </Box>
                }
            />

            <CardContent>
                <TableContainer component={Box}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCellBold>EVENT</TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        height={'100%'}
                                        display="flex"
                                        alignItems={'center'}
                                        justifyContent={'flex-end'}
                                    >
                                        <ScanIcon />
                                    </Box>
                                </TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        height={'100%'}
                                        display="flex"
                                        alignItems={'center'}
                                        justifyContent={'flex-end'}
                                    >
                                        <GoodIcon />
                                    </Box>
                                </TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        height={'100%'}
                                        display="flex"
                                        alignItems={'center'}
                                        justifyContent={'flex-end'}
                                    >
                                        <OnlineIcon />
                                    </Box>
                                </TableCellBold>
                                <TableCellBold align="right">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                    >
                                        WHEN
                                        <ArrowDropDownIcon fontSize="small" />
                                    </Box>
                                </TableCellBold>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {data.map((r, index) => (
                                <TableRow key={index}>
                                    <Tooltip message={r.event_name}>
                                        <EventTitleCell>
                                            <EventTitleText noWrap variant="body2">
                                                {r.event_name}
                                            </EventTitleText>
                                        </EventTitleCell>
                                    </Tooltip>
                                    <TableCell align="right">{r.scans_total}</TableCell>
                                    <TableCell align="right">
                                        {(r.scan_good_pct * 100).toFixed(1)}%
                                    </TableCell>
                                    <TableCell align="right">
                                        {(r.scan_online_pct * 100).toFixed(1)}%
                                    </TableCell>
                                    <TableCell align="right">
                                        {getRelativeTime(r.last_scan, 'short')}
                                    </TableCell>
                                </TableRow>
                            ))}

                            {data.length === 0 && (
                                <TableRow>
                                    <TableCell align="center" colSpan={5}>
                                        <Typography variant="subtitle2">No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Root>
    );
}

const Root = styled(Card)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
});

const Status = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
});

const TableCell = styled(_TableCell)(({ theme }) => ({
    whiteSpace: 'nowrap',
}));

const TableCellBold = styled(_TableCell)(({ theme }) => ({
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
}));

const EventTitleCell = styled(_TableCell)(({ theme }) => ({
    paddingRight: 0,
    maxWidth: 320,
    [theme.breakpoints.down('xl')]: {
        maxWidth: 160,
    },
    [theme.breakpoints.down('xl')]: {
        maxWidth: 100,
    },
    [theme.breakpoints.down('xl')]: {
        maxWidth: 220,
    },
}));

const EventTitleText = styled(Typography)({
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const ScanIcon = styled(CenterFocusStrongIcon)(({ theme }) => ({
    height: 18,
    width: 18,
}));

const GoodIcon = styled(CheckIcon)(({ theme }) => ({
    height: 18,
    width: 18,
}));

const OnlineIcon = styled(WifiIcon)(({ theme }) => ({
    height: 18,
    width: 18,
}));
